import WorkspaceCardGrid from "@/components/WorkspaceCardGrid";
import WorkspacesEmpty from "@/components/WorkspacesEmpty";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { Environment, graphql, usePaginationFragment } from "react-relay";
import { EntityWorkspacesPageFragment$key } from "./__generated__/EntityWorkspacesPageFragment.graphql";
import ErrorPage from "@/pages/_error";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { EntityWorkspacesPageQuery as EntityWorkspacesPageQueryType } from "./__generated__/EntityWorkspacesPageQuery.graphql";
const Fragment = graphql`
  fragment EntityWorkspacesPageFragment on Entity
  @refetchable(queryName: "EntityWorkspacesPageFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
  ) {
    workspaces(first: $count, after: $cursor)
      @connection(key: "EntityWorkspacesPageFragment_workspaces") {
      edges {
        node {
          id
          ...WorkspaceCardFragment
        }
      }
    }
  }
`;
export default function EntityWorkspacesPage() {
  const intl = useIntl();
  const {
    query: { entityByUsername, viewer },
    variables,
  } = useEntityWorkspacesPageQuery();
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    Fragment,
    entityByUsername as EntityWorkspacesPageFragment$key | null | undefined,
  );
  if (!data) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  if (!data.workspaces.edges.length) {
    return viewer?.canCreateWorkspace ? (
      <WorkspacesEmpty createWorkspacePath="/workspaces/mine/new" />
    ) : (
      <p className="text-gray-400">
        <FormattedMessage defaultMessage="No workspaces yet" />
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      <WorkspaceCardGrid
        edges={data.workspaces.edges}
        loadMore={loadNext}
        hasMore={hasNext}
        isLoading={isLoadingNext}
      />
    </div>
  );
}
const EntityWorkspacesPageQuery = graphql`
  query EntityWorkspacesPageQuery(
    $username: String!
    $actingAs: UsernameOrID!
  ) {
    viewer @ifAllowed {
      canCreateWorkspace: can(action: CREATE_WORKSPACE, actingAs: $actingAs)
    }
    entityByUsername(username: $username) {
      ...EntityWorkspacesPageFragment
    }
  }
`;
export function loader(environment: Environment) {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<EntityWorkspacesPageQueryType>(
      environment,
      EntityWorkspacesPageQuery,
      { username, actingAs: username },
    );
  };
}
const useEntityWorkspacesPageQuery = () =>
  usePreloaded<EntityWorkspacesPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Workspaces" }),
};
